class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());
    this._overlayClickHandler = this.close.bind(this);
    this.rebindOverlayClose();
    this.setHeaderCartIconAccessibility();
    this.bindUpsellButton();

    this.openDrawer = false; // sticky-true + renderContents reopen = ghost freeze
    this._hestiaClosing = false;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  rebindOverlayClose() {
    const overlay = this.querySelector("#CartDrawer-Overlay");
    if (!overlay) return;
    overlay.removeEventListener("click", this._overlayClickHandler);
    overlay.addEventListener("click", this._overlayClickHandler);
  }

	bindUpsellButton() {
		this.addEventListener("click", (event) => {
			const upsellButton = event.target.closest(".upsell-add-button");
			if (upsellButton && !upsellButton.disabled) {
				event.preventDefault();

				const form = upsellButton.closest(".upsell-form");
				const variantId = form.querySelector('input[name="id"]').value;

				this.addItemToCart(variantId, upsellButton);
			}
		});
	}

	async addItemToCart(variantId, button) {
		const sections = this.getSectionsToRender().map((section) => section.id);

		const body = {
			id: variantId,
			quantity: 1,
			sections: sections,
			sections_url: window.location.pathname,
		};

		// Deliberately not "is-loading": snippets/cart-drawer_style.liquid carries a bare
		// global `.is-loading { opacity: 0.8 !important }` for the legacy upsell buttons,
		// which would override whatever a block wants its busy state to look like.
		if (button) {
			button.disabled = true;
			button.classList.add("is-adding");
			button.setAttribute("aria-busy", "true");
		}

		try {
			const response = await window.CartApi.add(body);

			if (response.status === 422) {
				const errorData = await response.json();
				// Handle error (e.g., show a notification)
				console.error("Error adding item to cart:", errorData.description);
				return;
			}

			const parsedState = await response.json();
			this.renderContents(parsedState);
		} catch (error) {
			console.error("Error adding item to cart:", error);
		} finally {
			if (button && button.isConnected) {
				button.disabled = false;
				button.classList.remove("is-adding");
				button.removeAttribute("aria-busy");
			}
		}
	}

  /**
   * Refresh cart drawer after AJAX cart operations
   * Can be called directly or will be triggered by 'cart:ajax-update' event
   * @param {Object} sectionsData - Optional sections data from cart response
   */
  async refreshCartDrawer(sectionsData = null) {
    if (sectionsData && sectionsData.sections) {
      const dataToRender = {
        ...sectionsData,
        id: sectionsData.id || this.productId || null,
        item_count: sectionsData.item_count || 0
      };
      this.renderContents(dataToRender);
      return;
    }

    try {
      const sections = this.getSectionsToRender().map((section) => section.id);
      const url = `${window.location.pathname}?sections=${sections.join(',')}`;
      const response = await fetch(url);

      if (!response.ok) {
        return;
      }

      const parsedState = await response.json();

      // Wrap return in sections object for renderContents
      let sectionsData = null;
      if (parsedState.sections) {
        sectionsData = parsedState.sections;
      } else {
        sectionsData = {};
        const sectionIds = this.getSectionsToRender().map((section) => section.id);
        sectionIds.forEach((sectionId) => {
          if (parsedState[sectionId]) {
            sectionsData[sectionId] = parsedState[sectionId];
          }
        });
      }

      if (!sectionsData || Object.keys(sectionsData).length === 0) {
        console.error("No sections found in response:", parsedState);
        return;
      }

      const dataToRender = {
        sections: sectionsData,
        id: parsedState.id || this.productId || null,
        item_count: parsedState.item_count || 0
      };

      this.renderContents(dataToRender);
    } catch (error) {
      console.error("Error refreshing cart drawer:", error);
    }
  }

  startCountdownTimer() {
    const timerElement = document.getElementById("cart-countdown-timer");
    if (!timerElement) return;

    // Reset timer to 10 minutes
    let minutes = 10;
    let seconds = 0;

    // Clear any existing timer
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    // Update the timer display
    const updateTimer = () => {
      timerElement.textContent = `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    };

    // Initial display
    updateTimer();

    // Start the countdown
    this.countdownInterval = setInterval(() => {
      if (seconds === 0) {
        if (minutes === 0) {
          clearInterval(this.countdownInterval);
          return;
        }
        minutes--;
        seconds = 59;
      } else {
        seconds--;
      }

      updateTimer();
    }, 1000);
  }

  open(triggeredBy, { lockScroll = true, shouldTrapFocus = true } = {}) {
    if (this._hestiaClosing) return;
    this.openDrawer = true;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) this.setSummaryAccessibility(cartDrawerNote);
    if (this._hestiaOpenTimer) clearTimeout(this._hestiaOpenTimer);
    this._hestiaOpenTimer = setTimeout(() => {
      this._hestiaOpenTimer = null;
      if (this._hestiaClosing || this.openDrawer === false) return;
      this.classList.add("animate");
      void this.offsetWidth;
      requestAnimationFrame(() => {
        if (this._hestiaClosing || this.openDrawer === false) {
          this.classList.remove("active", "animate", "cart-drawer--editor-open", "cart-drawer--editor-no-transition");
          this.style.setProperty("pointer-events", "none", "important");
          return;
        }
        this.style.removeProperty("pointer-events");
        this.classList.add("active");
      });
    });

    let splideInitialized = false;
    const initSplides = () => {
      if (splideInitialized) return;
      splideInitialized = true;
      this.initUpsellSplide();
      this.initToggleSplides();
      this.initBannerSplide();
    };

    this.addEventListener(
      "transitionend",
      () => {
        if (shouldTrapFocus) {
          const containerToTrapFocusOn = this.classList.contains("is-empty")
            ? this.querySelector(".drawer__inner-empty")
            : document.getElementById("CartDrawer");
          const focusElement = this.querySelector(".drawer__inner") || this.querySelector(".drawer__close");
          trapFocus(containerToTrapFocusOn, focusElement);
        }

        initSplides();
      },
      { once: true },
    );

    setTimeout(initSplides, 500);

    if (lockScroll) {
      const y = window.scrollY || window.pageYOffset || 0;
      document.documentElement.dataset.hestiaScrollY = String(y);
      document.documentElement.classList.add("overflow-hidden", "hestia-cart-open", "hestia-scroll-locked");
      document.body.classList.add("overflow-hidden", "hestia-cart-open", "hestia-scroll-locked-fixed");
      document.body.style.top = "-" + y + "px";
    }

    // Start the countdown timer when drawer opens
    this.startCountdownTimer();

    this.dispatchEvent(new CustomEvent("cart-drawer:open", {
      bubbles: true,
      detail: { triggeredBy }
    }));
  }

  connectedCallback() {
    this.initUpsellSplideObserver();
    this.initToggleSplideObserver();
    this.initBannerSplide();
  }

  initBannerSplide() {
    const roots = this.querySelectorAll(".cart-drawer-banner-splide");
    if (!roots.length || !window.Splide) return;

    roots.forEach((root) => {
      const slides = root.querySelectorAll(".splide__slide");
      if (slides.length < 2) return;

      if (root._splide) {
        try {
          root._splide.destroy(false);
        } catch (e) {
          /* ignore */
        }
        root._splide = null;
      }

      const splide = new window.Splide(root, {
        type: "loop",
        perPage: 1,
        perMove: 1,
        gap: 0,
        arrows: true,
        pagination: true,
        drag: true,
        autoplay: false,
      });
      splide.mount();
      root._splide = splide;
    });
  }

  initUpsellSplideObserver() {
    const observer = new MutationObserver((mutations) => {
      let shouldInit = false;
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          const hasUpsellSplide = Array.from(mutation.addedNodes).some(
            (node) =>
              node.nodeType === 1 &&
              (node.classList?.contains("upsell-splide") ||
                (node.querySelector && node.querySelector(".upsell-splide"))),
          );
          if (hasUpsellSplide) {
            shouldInit = true;
            break;
          }
        }
      }
      if (shouldInit) {
        this.initUpsellSplide();
      }
    });

    observer.observe(this, { childList: true, subtree: true });
  }

  initToggleSplideObserver() {
    const observer = new MutationObserver((mutations) => {
      let shouldInit = false;
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          const hasToggleSplide = Array.from(mutation.addedNodes).some(
            (node) =>
              node.nodeType === 1 &&
              (node.classList?.contains("upsell-toggle-splide") ||
                (node.querySelector && node.querySelector(".upsell-toggle-splide"))),
          );
          if (hasToggleSplide) {
            shouldInit = true;
            break;
          }
        }
      }
      if (shouldInit) {
        this.initToggleSplides();
      }
    });

    observer.observe(this, { childList: true, subtree: true });
  }

  initUpsellSplide() {
    const roots = this.querySelectorAll(".upsell-splide");
    if (!roots.length) return;

    roots.forEach((root) => {
        const slides = root.querySelectorAll(".splide__slide");
        if (!slides.length) return;

        if (root._splide) {
          try {
            root._splide.destroy(false);
          } catch (e) {
            /* ignore */
          }
          root._splide = null;
        }

        const paginationType = root.dataset.paginationType || "bullets";
        const navStyle = root.dataset.navStyle || "inline";
        const headerNavDots = root.dataset.headerNav === "true";
        const slideCount = slides.length;
        const gap = Math.max(0, parseFloat(root.dataset.gap) || 0);
        const perViewDesktop =
          slideCount <= 1 ? 1 : Math.max(parseFloat(root.dataset.perViewDesktop) || 1.15, 1.05);
        const perViewMobile =
          slideCount <= 1 ? 1 : Math.max(parseFloat(root.dataset.perViewMobile) || 1.15, 1.05);

        const useOverlayArrows = navStyle === "overlay";
        const splidePagination =
          paginationType === "progressbar"
            ? false
            : paginationType === "bullets" && headerNavDots
              ? false
              : true;
        const splide = new window.Splide(root, {
          type: "slide",
          perPage: perViewMobile,
          perMove: 1,
          gap,
          arrows: useOverlayArrows,
          pagination: splidePagination,
          drag: true,
          mediaQuery: "min",
          breakpoints: {
            750: {
              perPage: perViewDesktop,
              gap,
            },
          },
        });

        const wrapper = root.closest(".cart-drawer-upsell-wrapper");
        const headerPrev = wrapper?.querySelector(".slide-arrow.prev");
        const headerNext = wrapper?.querySelector(".slide-arrow.next");
        const dots = wrapper ? Array.from(wrapper.querySelectorAll(".slide_item")) : [];

        const clearArrowInlineLock = (btn) => {
          if (!btn) return;
          btn.style.opacity = "";
          btn.style.cursor = "";
          btn.style.pointerEvents = "";
        };

        const syncHeaderNav = () => {
          const end = splide.Components.Controller.getEnd();
          const i = splide.index;
          if (headerPrev) {
            const atStart = i <= 0;
            headerPrev.classList.toggle("is-disabled", atStart);
            headerPrev.setAttribute("aria-disabled", atStart ? "true" : "false");
            if (!atStart) clearArrowInlineLock(headerPrev);
          }
          if (headerNext) {
            const atEnd = i >= end;
            headerNext.classList.toggle("is-disabled", atEnd);
            headerNext.setAttribute("aria-disabled", atEnd ? "true" : "false");
            if (!atEnd) clearArrowInlineLock(headerNext);
          }
          dots.forEach((dot, idx) => dot.classList.toggle("active", idx === i));
        };

        splide.on("mounted moved resized", syncHeaderNav);

        if (!useOverlayArrows) {
          if (headerPrev) {
            headerPrev.onclick = (e) => {
              e.preventDefault();
              e.stopPropagation();
              if (headerPrev.classList.contains("is-disabled")) return;
              splide.go("<");
            };
          }
          if (headerNext) {
            headerNext.onclick = (e) => {
              e.preventDefault();
              e.stopPropagation();
              if (headerNext.classList.contains("is-disabled")) return;
              splide.go(">");
            };
          }
        }

        splide.mount();
        root._splide = splide;
        syncHeaderNav();

        if (paginationType === "progressbar") {
          root.querySelector(".upsell-splide__progress")?.remove();
          const bar = document.createElement("div");
          bar.className = "upsell-splide__progress";
          const fill = document.createElement("div");
          fill.className = "upsell-splide__progress-fill";
          bar.appendChild(fill);
          root.appendChild(bar);

          const updateProgress = () => {
            const end = splide.Components.Controller.getEnd();
            const thumbW = end > 0 ? 1 / (end + 1) : 1;
            const thumbL = end > 0 ? (splide.index / end) * (1 - thumbW) : 0;
            fill.style.width = `${thumbW * 100}%`;
            fill.style.left = `${thumbL * 100}%`;
          };
          splide.on("moved resized", updateProgress);
          updateProgress();

          bar.addEventListener("click", (e) => {
            const rect = bar.getBoundingClientRect();
            const ratio = (e.clientX - rect.left) / rect.width;
            const end = splide.Components.Controller.getEnd();
            splide.go(Math.round(ratio * end));
          });
        }

        syncHeaderNav();
      });
  }

  initToggleSplides() {
    const toggleRoots = this.querySelectorAll(".upsell-toggle-splide");
    if (!toggleRoots.length) return;

    toggleRoots.forEach((root) => {
        const inputId = root.getAttribute("data-input-id");
        const checkbox = inputId ? document.getElementById(inputId) : null;

        if (checkbox && root._toggleCheckboxHandler) {
          checkbox.removeEventListener("change", root._toggleCheckboxHandler);
          root._toggleCheckboxHandler = null;
        }
        if (root._toggleClickHandler) {
          root.removeEventListener("click", root._toggleClickHandler);
          root._toggleClickHandler = null;
        }

        if (root._toggleSplide) {
          try {
            root._toggleSplide.destroy(false);
          } catch (e) {
            /* ignore */
          }
          root._toggleSplide = null;
        }

        const initialIndex = checkbox && checkbox.checked ? 1 : 0;

        const splide = new window.Splide(root, {
          start: initialIndex,
          type: "slide",
          perPage: 1,
          gap: 0,
          arrows: false,
          pagination: false,
          drag: true,
          speed: 300,
        });

        const applyActiveClass = () => {
          root.classList.toggle("is-active", splide.index === 1);
        };

        splide.on("mounted", applyActiveClass);

        splide.on("moved", () => {
          const on = splide.index === 1;
          applyActiveClass();
          if (checkbox && checkbox.checked !== on) {
            checkbox.checked = on;
            checkbox.dispatchEvent(new Event("change", { bubbles: true }));
          }
        });

        if (checkbox) {
          const checkboxHandler = () => {
            const want = checkbox.checked ? 1 : 0;
            if (splide.index !== want) splide.go(want);
            applyActiveClass();
          };
          root._toggleCheckboxHandler = checkboxHandler;
          checkbox.addEventListener("change", checkboxHandler);
        }

        const clickHandler = () => {
          splide.go(splide.index === 0 ? 1 : 0);
        };
        root._toggleClickHandler = clickHandler;
        root.addEventListener("click", clickHandler);

        splide.mount();
        root._toggleSplide = splide;
      });
  }

  close() {
    this.openDrawer = false;
    this._hestiaClosing = true;
    if (this._hestiaOpenTimer) {
      clearTimeout(this._hestiaOpenTimer);
      this._hestiaOpenTimer = null;
    }
    this.classList.remove("active", "animate", "cart-drawer--editor-open");
    this.style.setProperty("pointer-events", "none", "important");
    this.dispatchEvent(new CustomEvent("cart-drawer:close", { bubbles: true }));
    try { removeTrapFocus(this.activeElement); } catch (e) {}
    const html = document.documentElement;
    const body = document.body;
    html.classList.remove("overflow-hidden", "hestia-cart-open", "hestia-scroll-locked");
    body.classList.remove("overflow-hidden", "hestia-cart-open", "hestia-scroll-locked-fixed");
    body.style.top = "";
    body.style.pointerEvents = "";
    html.style.pointerEvents = "";
    if (html.dataset.hestiaScrollY != null) {
      const y = parseInt(html.dataset.hestiaScrollY || "0", 10) || 0;
      delete html.dataset.hestiaScrollY;
      try { window.scrollTo(0, y); } catch (e) {}
    }
    try {
      if (typeof window.__hestiaUnlockPageScroll === "function") window.__hestiaUnlockPageScroll();
    } catch (e) {}
    if (this.countdownInterval) clearInterval(this.countdownInterval);
    const self = this;
    setTimeout(function () { self._hestiaClosing = false; }, 500);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute("aria-controls", cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute("aria-expanded", !event.currentTarget.closest("details").hasAttribute("open"));
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
			if (section.selector == "#CartDrawer") {
        const newCartDrawer = this.getSectionDOM(parsedState.sections[section.id], section.selector);
        updateCartDrawer(sectionElement, newCartDrawer);
      } else {
        if (section.id === "cart-icon-bubble") {
          const iconType = sectionElement.getAttribute("data-cart-icon-type");
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
          if (iconType) {
            sectionElement.setAttribute("data-cart-icon-type", iconType);
          }
          const iconWrapper = sectionElement.querySelector(".svg-wrapper");
          if (iconWrapper && iconType) {
            const bagIcon = iconWrapper.querySelector(".cart-icon-bag");
            const cartIcon = iconWrapper.querySelector(".cart-icon-cart");
            const cartIconEmpty = iconWrapper.querySelector(".cart-icon-cart-empty");
            const customIcon = iconWrapper.querySelector(".cart-icon-custom");

            if (iconType === "bag") {
              if (bagIcon) bagIcon.style.display = "flex";
            } else if (iconType === "custom") {
              const customSvgSource = document.getElementById("header-cart-custom-svg");
              if (customIcon && customSvgSource) customIcon.innerHTML = customSvgSource.innerHTML;
              if (customIcon) customIcon.style.display = "flex";
            } else {
              const cartEmpty = parsedState.item_count === 0;
              if (cartEmpty && cartIconEmpty) {
                cartIconEmpty.style.display = "flex";
              } else if (cartIcon) {
                cartIcon.style.display = "flex";
              }
            }
          }
        } else {
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        }
      }
    });

    setTimeout(() => {
      this.rebindOverlayClose();

      this.dispatchEvent(new CustomEvent("cart-drawer:render", {
        bubbles: true,
        detail: { parsedState }
      }));
     
      if (this.openDrawer && !this._hestiaClosing) this.open();

      this.initUpsellSplide();
      this.initToggleSplides();
      this.initBannerSplide();
    });

    // Check if jQuery and slick are loaded before initializing - REMOVED OLD SLICK CODE
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
				id: window.getCartDrawerSectionHandle?.() || this.dataset.sectionHandle || "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

function setupAjaxCartHandler() {
  const cartDrawer = document.querySelector("cart-drawer");
  if (!cartDrawer) return;

  document.addEventListener("cart:ajax-update", async function (event) {
    const sectionsData = event.detail?.sectionsData || null;
    await cartDrawer.refreshCartDrawer(sectionsData);
  });

  window.refreshCartDrawer = async function (sectionsData = null) {
    const drawer = document.querySelector("cart-drawer");
    if (drawer) {
      await drawer.refreshCartDrawer(sectionsData);
    }
  };
}

if (document.readyState === 'loading') {
  document.addEventListener("DOMContentLoaded", setupAjaxCartHandler);
} else {
  setupAjaxCartHandler();
}

// Wait for CartItems to be defined before extending it
if (typeof CartItems !== "undefined") {
  // CartItems is already loaded
  defineCartDrawerItems();
} else {
  // Wait for cart.js to load and define CartItems
  const checkCartItems = setInterval(() => {
    if (typeof CartItems !== "undefined") {
      clearInterval(checkCartItems);
      defineCartDrawerItems();
    }
  }, 50);
}

function defineCartDrawerItems() {
  if (customElements.get("cart-drawer-items")) {
    return; // Already defined
  }

  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: "CartDrawer",
					section: window.getCartDrawerSectionHandle?.() || "cart-drawer",
          selector: ".drawer__inner",
        },
        {
          id: "cart-icon-bubble",
          section: "cart-icon-bubble",
          selector: ".shopify-section",
        },
      ];
    }
  }

  customElements.define("cart-drawer-items", CartDrawerItems);
}
